import { useQuery } from "@tanstack/react-query";
import { LayoutGrid, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { AppTile } from "../components/AppTile";
import { Alert, Card, EmptyState, Input, PageHeader, Skeleton } from "../components/ui";
import { api, errorMessage } from "../lib/api";
import type { LauncherApp } from "../lib/types";

export function AppsPage() {
  const apps = useQuery({ queryKey: ["launcher"], queryFn: () => api.get<{ items: LauncherApp[] }>("/api/v1/dashboard/apps") });
  const [q, setQ] = useState("");
  const groups = useMemo(() => {
    const term = q.trim().toLowerCase();
    const list = (apps.data?.items ?? []).filter((a) => !term || `${a.name} ${a.description} ${a.category}`.toLowerCase().includes(term));
    const map = new Map<string, LauncherApp[]>();
    for (const a of list) map.set(a.category, [...(map.get(a.category) ?? []), a]);
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [apps.data, q]);
  const total = apps.data?.items.length ?? 0;

  return (
    <div>
      <PageHeader title="Apps" description="Every application you can use, with single sign-on through Galeway." />
      {total > 0 && (
        <div className="relative mb-6 max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-subtle" aria-hidden />
          <Input
            type="search"
            aria-label="Search applications"
            placeholder="Search applications"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
          />
        </div>
      )}
      {apps.isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-[132px] rounded-xl" />
          ))}
        </div>
      ) : apps.isError ? (
        <Alert tone="danger">{errorMessage(apps.error)}</Alert>
      ) : total === 0 ? (
        <Card>
          <EmptyState
            icon={<LayoutGrid className="size-5" />}
            title="No applications yet"
            description="Ask your administrator for access to the applications you need."
          />
        </Card>
      ) : groups.length === 0 ? (
        <EmptyState icon={<Search className="size-5" />} title="No matching applications" description={`Nothing matches “${q}”.`} />
      ) : (
        <div className="space-y-8">
          {groups.map(([category, list]) => (
            <section key={category} aria-label={category}>
              <h2 className="mb-3 text-[12px] font-semibold uppercase tracking-wider text-subtle">{category}</h2>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
                {list.map((a) => (
                  <AppTile key={a.id} app={a} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
